import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { MasterUser } from '@/types/userRole';

export const useMasterUsers = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState<MasterUser[]>([
    {
      id: '1',
      name: 'John Doe',
      email: 'john@example.com',
      role: 'admin',
      status: 'active',
    },
    {
      id: '2',
      name: 'Jane Smith',
      email: 'jane@example.com',
      role: 'user',
      status: 'pending',
    },
    {
      id: '3',
      name: 'Michael Johnson',
      email: 'michael@example.com',
      role: 'manager',
      status: 'active',
    },
    {
      id: '4',
      name: 'Sarah Williams',
      email: 'sarah@example.com',
      role: 'user',
      status: 'suspended',
    },
    {
      id: '5',
      name: 'Robert Davis',
      email: 'robert@example.com',
      role: 'superadmin',
      status: 'active',
    },
    {
      id: '6',
      name: 'Lisa Cooper',
      email: 'lisa@example.com',
      role: 'reseller',
      status: 'active',
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      return users;
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive',
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [users, toast]);

  useEffect(() => {
    loadUsers();
  }, []);

  const inviteUser = async (data: { email: string; role: string; name: string }): Promise<MasterUser> => {
    try {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newUser: MasterUser = {
        id: Date.now().toString(),
        name: data.name,
        email: data.email,
        role: data.role as any,
        status: 'pending',
      };
      
      setUsers(prevUsers => [...prevUsers, newUser]);
      
      toast({
        title: "Invitation Sent",
        description: `Successfully invited ${data.email}`,
      });
      setShowInviteDialog(false);
      return newUser;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send invitation",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const removeUser = async (userId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      
      toast({
        title: "User Removed",
        description: "User has been successfully removed",
      });
      
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove user",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = async (userId: string, userData: Partial<MasterUser>): Promise<boolean> => {
    try {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === userId ? { ...user, ...userData } : user
      ));
      
      const updatedUser = users.find(user => user.id === userId);
      const updateType = Object.keys(userData)[0] || 'profile';
      
      toast({
        title: `${updateType.charAt(0).toUpperCase() + updateType.slice(1)} Updated`,
        description: `${updatedUser?.name}'s ${updateType} has been updated successfully`,
      });
      
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to update user ${Object.keys(userData)[0] || 'profile'}`,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    users,
    isLoading,
    inviteUser,
    removeUser,
    updateUser,
    showInviteDialog,
    setShowInviteDialog,
    loadUsers,
  };
};
